import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { updateUserProfile } from "../api/user";
import { useLanguage } from "../contexts/LanguageContext";
import "../styles/main.css";

const translations = {
  en: {
    setPasswordTitle: "Set Your Password",
    setNameTitle: "About You",
    setBirthTimeTitle: "When were you born?",
    passwordLabel: "New Password",
    confirmPasswordLabel: "Confirm Password",
    nameLabel: "What is your name? (Optional)",
    birthTimeLabel: "Enter your birth time (Optional)",
    yearLabel: "Year",
    monthLabel: "Month",
    dayLabel: "Day",
    hourLabel: "Hour (0-23)",
    setPasswordBtn: "Set Password",
    saveFinishBtn: "Continue",
    finishBtn: "Finish",
    skipBtn: "Skip",
    passwordComplexity: "Password must be at least 8 characters and include both letters and numbers.",
    passwordMismatch: "Passwords do not match.",
    successPassword: "Password set successfully!",
    error: "An error occurred. Please try again.",
    processing: "Processing...",
  },
  zh: {
    setPasswordTitle: "设置密码",
    setNameTitle: "关于你",
    setBirthTimeTitle: "你的出生时间",
    passwordLabel: "新密码",
    confirmPasswordLabel: "确认密码",
    nameLabel: "请输入你的名字（选填）",
    birthTimeLabel: "请输入出生时间（选填）",
    yearLabel: "年",
    monthLabel: "月",
    dayLabel: "日",
    hourLabel: "时 (0-23)",
    setPasswordBtn: "设置密码",
    saveFinishBtn: "继续",
    finishBtn: "完成",
    skipBtn: "跳过",
    passwordComplexity: "密码长度至少为8个字符，且包含字母和数字。",
    passwordMismatch: "两次输入的密码不一致。",
    successPassword: "密码设置成功！",
    error: "发生错误，请重试。",
    processing: "处理中...",
  }
};

export default function UpdatePasswordPage({ session }) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  const [step, setStep] = useState(1); // 1: Password, 2: Name, 3: Birth Time
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  
  // Birth time state
  const [birthYear, setBirthYear] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthHour, setBirthHour] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Check if user already has a profile (means they're resetting password, not new signup)
    const checkUserProfile = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        // Give it a moment or redirect
        // navigate("/"); 
        return;
      }

      // Security Check: Ensure the session email matches the email in the URL (if present)
      // This prevents users from setting a password for the wrong account if they were already logged in
      const params = new URLSearchParams(window.location.search);
      const emailParam = params.get("email");
      
      if (emailParam && data.session.user.email && emailParam.toLowerCase() !== data.session.user.email.toLowerCase()) {
        console.warn("Session email mismatch. Signing out.");
        await supabase.auth.signOut();
        window.location.reload();
        return;
      }
    };
    checkUserProfile();
  }, [navigate]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setMessage(t.passwordMismatch);
      return;
    }

    const hasLetter = /[a-zA-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const isLongEnough = password.length >= 8;

    if (!isLongEnough || !hasLetter || !hasDigit) {
      setMessage(t.passwordComplexity);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const { error } = await supabase.auth.updateUser({ password: password });
      if (error) throw error;
      
      // Ensure profile exists and preserve language; do not redirect
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await updateUserProfile(session.access_token, "", language);
      }

      setMessage(t.successPassword);

      // Always proceed to optional steps (Name, Birth Time)
      setTimeout(() => {
        setStep(2);
        setMessage("");
        setLoading(false);
      }, 800);
    } catch (err) {
      console.error(err);
      setMessage(err.message || t.error);
      setLoading(false);
    }
  };

  const handleSetName = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (name.trim()) {
        // We need the access token
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
            await updateUserProfile(session.access_token, name);
        }
      }
      // Move to next step instead of finishing
      setStep(3);
      setLoading(false);
    } catch (err) {
      console.error(err);
      // Even if name fails, we can probably let them through or show error
      setMessage(t.error);
      setLoading(false);
    }
  };

  const handleSetBirthTime = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Construct date string if all parts are present
      // Format: YYYY-MM-DD HH:00:00
      let dateOfBirth = null;
      if (birthYear && birthMonth && birthDay && birthHour) {
        // Pad with leading zeros
        const m = birthMonth.toString().padStart(2, '0');
        const d = birthDay.toString().padStart(2, '0');
        const h = birthHour.toString().padStart(2, '0');
        dateOfBirth = `${birthYear}-${m}-${d} ${h}:00:00`;
      }

      if (dateOfBirth) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
            // Pass existing name to avoid overwriting it with empty string if not provided here
            // But updateUserProfile handles partial updates if we structured it that way.
            // Our current updateUserProfile expects (token, name, language, dob).
            // We should probably pass the current name state.
            await updateUserProfile(session.access_token, name, null, dateOfBirth);
        }
      }

      // Force reload to update session state in App.js
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      setMessage(t.error);
      setLoading(false);
    }
  };

  return (
    <div className="desti-container">
      <div className="page-content" style={{ maxWidth: "400px", margin: "0 auto", width: "100%" }}>
        <div className="card" style={{ position: "relative" }}>
          {step === 1 ? (
            <>
              <h1 style={{ textAlign: "center", marginBottom: "24px" }}>{t.setPasswordTitle}</h1>
              <form onSubmit={handleSetPassword}>
                <label className="auth-label">{t.passwordLabel}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="******"
                  required
                  style={{ width: "100%", padding: "10px", marginBottom: "20px", borderRadius: "6px", border: "1px solid #ddd" }}
                />

                <label className="auth-label">{t.confirmPasswordLabel}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="******"
                  required
                  style={{ width: "100%", padding: "10px", marginBottom: "20px", borderRadius: "6px", border: "1px solid #ddd" }}
                />

                <button type="submit" className="primary" disabled={loading} style={{ width: "100%", padding: "12px" }}>
                  {loading ? t.processing : t.setPasswordBtn}
                </button>
              </form>
            </>
          ) : step === 2 ? (
            <>
              <button 
                type="button" 
                onClick={() => setStep(3)} 
                style={{ 
                  position: "absolute", 
                  top: "2px", 
                  right: "4px", 
                  width: "90px",
                  background: "transparent", 
                  border: "none", 
                  textDecoration: "underline",
                  color: "#00b3ff", 
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}
              >
                {t.skipBtn}
              </button>

              <h1 style={{ textAlign: "center", marginBottom: "24px", marginTop: "20px" }}>{t.setNameTitle}</h1>
              <form onSubmit={handleSetName}>
                <label className="auth-label">{t.nameLabel}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  style={{ width: "100%", padding: "10px", marginBottom: "20px", borderRadius: "6px", border: "1px solid #ddd" }}
                />
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button 
                    type="submit" 
                    className="primary" 
                    disabled={loading} 
                    style={{ 
                      width: "100%", 
                      padding: "12px",
                      opacity: loading ? 0.6 : 1,
                      cursor: loading ? "not-allowed" : "pointer"
                    }}
                  >
                    {loading ? t.processing : t.saveFinishBtn}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <button 
                type="button" 
                onClick={() => { window.location.href = "/"; }} 
                style={{ 
                  position: "absolute", 
                  top: "2px", 
                  right: "4px", 
                  width: "90px",
                  background: "transparent", 
                  border: "none", 
                  textDecoration: "underline",
                  color: "#00b3ff", 
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}
              >
                {t.skipBtn}
              </button>

              <h1 style={{ textAlign: "center", marginBottom: "24px", marginTop: "20px" }}>{t.setBirthTimeTitle}</h1>
              <form onSubmit={handleSetBirthTime}>
                <label className="auth-label">{t.birthTimeLabel}</label>
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
                  <div>
                    <label style={{ fontSize: "0.8rem", color: "#666" }}>{t.yearLabel}</label>
                    <input
                      type="number"
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value)}
                      placeholder="YYYY"
                      style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.8rem", color: "#666" }}>{t.monthLabel}</label>
                    <input
                      type="number"
                      min="1" max="12"
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      placeholder="MM"
                      style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.8rem", color: "#666" }}>{t.dayLabel}</label>
                    <input
                      type="number"
                      min="1" max="31"
                      value={birthDay}
                      onChange={(e) => setBirthDay(e.target.value)}
                      placeholder="DD"
                      style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: "0.8rem", color: "#666" }}>{t.hourLabel}</label>
                    <input
                      type="number"
                      min="0" max="23"
                      value={birthHour}
                      onChange={(e) => setBirthHour(e.target.value)}
                      placeholder="HH"
                      style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                    />
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                  <button 
                    type="submit" 
                    className="primary" 
                    disabled={loading || !birthYear || !birthMonth || !birthDay || !birthHour} 
                    style={{ 
                      width: "100%", 
                      padding: "12px",
                      opacity: (!birthYear || loading) ? 0.6 : 1,
                      cursor: (!birthYear || loading) ? "not-allowed" : "pointer"
                    }}
                  >
                    {loading ? t.processing : t.finishBtn}
                  </button>
                </div>
              </form>
            </>
          )}

          {message && (
            <div style={{ marginTop: "16px", textAlign: "center", color: message === t.successPassword ? "#10b981" : "#ef4444" }}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
